/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import {
  Form, Button, Row, Col, Alert, Card,
} from 'react-bootstrap';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import BillingoService from '../../Services/BillingoService';
import { COUNTRY_CODES, normalizeCountryCode } from '../../Utils/countryCodes';

// Billingo partner tax_type (Hungarian taxation / entity form).
const TAX_TYPES = [
  { value: '', label: '— Not specified' },
  { value: 'private_person', label: 'Magánszemély (private individual)' },
  { value: 'aa', label: 'Alanyi adómentes (AAM)' },
  { value: 'ag', label: 'Átalányadózó' },
  { value: 'eva', label: 'EVA' },
  { value: 'szja', label: 'SZJA' },
  { value: 'kkt', label: 'Közkereseti társaság (Kkt.)' },
  { value: 'ltd', label: 'Kft. (limited liability)' },
  { value: 'plc', label: 'Rt. (company limited by shares)' },
  { value: 'ose', label: 'Őstermelő' },
  { value: 'katego', label: 'Kategorikus' },
];

// Step 2 — pick an existing Billingo account (search) or create a new one.
// Accounts live in Billingo; we never persist our own copy. Companies require
// a VAT (tax) number and registration id.
function BillingoAccountSelector({
  value = null, onChange, defaultBilling = null, suggestions = [], note = '',
}) {
  const [isSearching, setIsSearching] = useState(false);
  const [options, setOptions] = useState([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [saving, setSaving] = useState(false);

  const blankNew = {
    type: 'company',
    name: defaultBilling?.name || '',
    emails: '',
    tax_number: '',
    registration_number: '',
    tax_type: '',
    country_code: normalizeCountryCode(defaultBilling?.country) || 'HU',
    post_code: defaultBilling?.postcode || '',
    city: defaultBilling?.city || '',
    address: defaultBilling?.address || '',
  };
  const [draft, setDraft] = useState(blankNew);

  const handleSearch = async (query) => {
    setIsSearching(true);
    try {
      setOptions(await BillingoService.searchAccounts(query));
    } finally {
      setIsSearching(false);
    }
  };

  const isCompany = draft.type === 'company';
  // Billingo requires a full address on every partner (post_code, city, street).
  const draftValid = draft.name.trim()
    && draft.post_code.trim() && draft.city.trim() && draft.address.trim()
    && (!isCompany || (draft.tax_number.trim() && draft.registration_number.trim()));

  const handleCreate = async () => {
    setCreateError('');
    setSaving(true);
    try {
      const account = await BillingoService.createAccount({
        type: draft.type,
        name: draft.name.trim(),
        emails: draft.emails.split(',').map((e) => e.trim()).filter(Boolean),
        tax_number: isCompany ? draft.tax_number.trim() : null,
        registration_number: isCompany ? draft.registration_number.trim() : null,
        tax_type: draft.tax_type || null,
        address: {
          country_code: draft.country_code,
          post_code: draft.post_code,
          city: draft.city,
          address: draft.address,
        },
      });
      onChange(account);
      setCreating(false);
    } catch (err) {
      setCreateError(err.response?.data?.message || err.message || 'Failed to create account');
    } finally {
      setSaving(false);
    }
  };

  const setField = (field) => (e) => setDraft((d) => ({ ...d, [field]: e.target.value }));

  if (value) {
    return (
      <Card border="success">
        <Card.Body className="d-flex justify-content-between align-items-start">
          <div>
            <div className="fw-semibold">{value.name}</div>
            {value.tax_number && (
              <div className="small text-muted">
                VAT:
                {' '}
                {value.tax_number}
                {value.registration_number ? ` · Reg: ${value.registration_number}` : ''}
              </div>
            )}
            {(value.emails || []).length > 0 && (
              <div className="small text-muted">{value.emails.join(', ')}</div>
            )}
            {note && (
              <div className="small text-success mt-1">
                <i className="fa fa-clock-rotate-left me-1" />
                {note}
              </div>
            )}
            <div className="small text-muted">
              {[value.address?.post_code, value.address?.city, value.address?.country_code]
                .filter(Boolean).join(' ')}
            </div>
          </div>
          <Button variant="outline-secondary" size="sm" onClick={() => onChange(null)}>
            Change
          </Button>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div>
      {!creating && (
        <>
          {suggestions.length > 0 && (
            <div className="mb-3">
              <Form.Label className="mb-1">
                Existing Billingo accounts for this customer
              </Form.Label>
              <div className="text-muted small mb-2">
                Matched on the order&apos;s billing name, email or passenger name —
                pick one instead of creating a duplicate party.
              </div>
              <div className="d-flex flex-wrap gap-2">
                {suggestions.map((a) => (
                  <Button
                    key={a.id}
                    variant="outline-primary"
                    size="sm"
                    className="text-start"
                    onClick={() => onChange(a)}
                  >
                    <span className="fw-semibold">{a.name}</span>
                    <span className="text-muted">
                      {a.tax_number ? ` · ${a.tax_number}` : ''}
                      {a.emails?.[0] ? ` · ${a.emails[0]}` : ''}
                    </span>
                  </Button>
                ))}
              </div>
              <hr />
            </div>
          )}

          <Form.Label>Search Billingo accounts</Form.Label>
          <AsyncTypeahead
            id="billingo-account-search"
            isLoading={isSearching}
            minLength={1}
            onSearch={handleSearch}
            options={options}
            labelKey="name"
            placeholder="Search by name, VAT number or email…"
            onChange={(selected) => selected[0] && onChange(selected[0])}
            renderMenuItemChildren={(option) => (
              <div>
                <div>{option.name}</div>
                <small className="text-muted">
                  {option.tax_number || option.type}
                  {option.emails?.[0] ? ` · ${option.emails[0]}` : ''}
                </small>
              </div>
            )}
          />
          <div className="mt-3 text-muted small">Can’t find the account?</div>
          <Button variant="outline-primary" size="sm" className="mt-1" onClick={() => setCreating(true)}>
            <i className="fa fa-plus me-1" />
            Create new Billingo account
          </Button>
        </>
      )}

      {creating && (
        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <strong>New Billingo account</strong>
              <Button variant="link" size="sm" onClick={() => setCreating(false)}>Back to search</Button>
            </div>

            {createError && <Alert variant="danger">{createError}</Alert>}

            <Form.Group className="mb-3">
              <Form.Check
                inline
                type="radio"
                label="Company"
                id="acct-company"
                checked={isCompany}
                onChange={() => setDraft((d) => ({ ...d, type: 'company' }))}
              />
              <Form.Check
                inline
                type="radio"
                label="Individual"
                id="acct-individual"
                checked={!isCompany}
                onChange={() => setDraft((d) => ({ ...d, type: 'individual' }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {isCompany ? 'Company name' : 'Full name'}
                {' '}
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control value={draft.name} onChange={setField('name')} />
            </Form.Group>

            {isCompany && (
              <Row>
                <Form.Group as={Col} md={6} className="mb-3">
                  <Form.Label>
                    VAT / tax number
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control value={draft.tax_number} onChange={setField('tax_number')} placeholder="12345678-2-41" />
                </Form.Group>
                <Form.Group as={Col} md={6} className="mb-3">
                  <Form.Label>
                    Registration id
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control value={draft.registration_number} onChange={setField('registration_number')} placeholder="01-09-123456" />
                </Form.Group>
              </Row>
            )}

            <Form.Group className="mb-3">
              <Form.Label>
                Email(s)
                <span className="text-muted small">(optional)</span>
              </Form.Label>
              <Form.Control value={draft.emails} onChange={setField('emails')} placeholder="ap@company.com, finance@company.com" />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Tax type
                <span className="text-muted small">(optional)</span>
              </Form.Label>
              <Form.Select value={draft.tax_type} onChange={setField('tax_type')}>
                {TAX_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Form.Select>
            </Form.Group>

            <Row>
              <Form.Group as={Col} md={3} className="mb-3">
                <Form.Label>
                  Country
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select value={draft.country_code} onChange={setField('country_code')}>
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={c.code}>{`${c.code} — ${c.name}`}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group as={Col} md={3} className="mb-3">
                <Form.Label>
                  Post code
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control value={draft.post_code} onChange={setField('post_code')} />
              </Form.Group>
              <Form.Group as={Col} md={6} className="mb-3">
                <Form.Label>
                  City
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control value={draft.city} onChange={setField('city')} />
              </Form.Group>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>
                Address (street)
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control value={draft.address} onChange={setField('address')} />
            </Form.Group>

            <Button onClick={handleCreate} disabled={!draftValid || saving}>
              {saving ? 'Creating…' : 'Create account'}
            </Button>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}

export default BillingoAccountSelector;
